import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, DollarSign, Package, RefreshCw, Search } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import MobileNav from '../components/MobileNav';
import OrderComplaintSection from '../components/OrderComplaintSection';
import { apiFetch } from '../lib/apiClient';
import { formatOrderNumberLabel } from '../lib/orderUtils';
import {
  formatOrderPickup,
  getOrderReadiness,
  isActiveOrder,
  orderItemsSummary,
  sortOrdersByClosestPickup,
} from '../lib/orderStatus';

function OrderCard({ order, onComplaintChanged }) {
  const readiness = getOrderReadiness(order);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-xl p-4 shadow-sm"
      data-testid={`order-card-${order.order_id}`}
    >
      <Link to={`/orders/${order.order_id}`} className="block hover:opacity-90" data-testid={`order-${order.order_id}`}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <h3 className="font-medium truncate">{order.truck_name || 'Food truck'}</h3>
            <p className="text-sm text-muted-foreground">
              Order {formatOrderNumberLabel(order)} · {orderItemsSummary(order)}
            </p>
          </div>
          <Badge className={readiness.badgeClass} data-testid={`order-readiness-${order.order_id}`}>
            {readiness.label}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{readiness.detail}</p>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {formatOrderPickup(order.scheduled_pickup_time || order.created_at)}
          </span>
          <span className="flex items-center gap-1">
            <DollarSign className="w-4 h-4" />
            CA$ {Number(order.total_amount || 0).toFixed(2)}
          </span>
        </div>
      </Link>
      <OrderComplaintSection order={order} compact onChanged={onComplaintChanged} />
    </motion.div>
  );
}

function OrderGroup({ title, orders, onComplaintChanged }) {
  if (!orders.length) return null;
  return (
    <section className="mb-8" data-testid={`orders-group-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h2 className="text-lg font-bold mb-4">{title}</h2>
      <div className="space-y-4">
        {orders.map((order) => (
          <OrderCard key={order.order_id} order={order} onComplaintChanged={onComplaintChanged} />
        ))}
      </div>
    </section>
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      const response = await apiFetch('/api/orders');
      if (!response.ok) throw new Error('Could not load your orders');
      const data = await response.json();
      setOrders(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      if (!silent) setError(err.message || 'Could not load your orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') fetchOrders({ silent: true });
    };
    const interval = setInterval(refreshWhenVisible, 15000);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [fetchOrders]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase().replace(/^#/, '');
    if (!needle) return orders;
    return orders.filter((order) => {
      const haystack = [
        order.order_number,
        order.order_id,
        order.truck_name,
        order.status,
        orderItemsSummary(order),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [orders, query]);

  const active = useMemo(
    () => sortOrdersByClosestPickup(filtered.filter(isActiveOrder)),
    [filtered],
  );
  const past = useMemo(
    () => sortOrdersByClosestPickup(filtered.filter((order) => !isActiveOrder(order))),
    [filtered],
  );

  return (
    <div className="min-h-screen bg-background pb-safe">
      <Header />
      <div className="max-w-lg mx-auto px-4 pt-20 pb-24">
        <Link to="/profile" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to profile
        </Link>

        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold">Your orders</h1>
            <p className="text-sm text-muted-foreground">Track pickup readiness and order history.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => fetchOrders({ silent: true })}
            disabled={refreshing}
            aria-label="Refresh orders"
            data-testid="refresh-orders-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="relative mb-6">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search order #, truck, or item"
            className="pl-9"
            data-testid="orders-search-input"
          />
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-xl p-4 animate-pulse">
                <div className="h-5 bg-muted rounded w-1/2 mb-2" />
                <div className="h-4 bg-muted rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => fetchOrders()}>Try again</Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{orders.length ? 'No orders match your search.' : 'No orders yet'}</p>
            <Link to="/" className="text-primary hover:underline text-sm mt-2 inline-block">
              Browse food trucks
            </Link>
          </div>
        ) : (
          <>
            <OrderGroup title="Active orders" orders={active} onComplaintChanged={() => fetchOrders({ silent: true })} />
            <OrderGroup title="Past orders" orders={past} onComplaintChanged={() => fetchOrders({ silent: true })} />
          </>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
